import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { User } from 'firebase/auth'
import { auth } from '@/services/firebase/config'
import { provisionProfessorAccount } from '@/services/accountProvisioning'
import {
  callAcceptLegalTerms,
  fetchCurrentLegalVersion,
  fetchUserLegalState,
  getLegalTermsErrorCode,
  type CurrentLegalVersion,
} from '@/services/legalConsentService'
import type { LegalChannel } from '@/types/legal'

/**
 * Estado del consentimiento legal de la sesión (HU-AUTH-005, ADR-SEC-018).
 *
 * Lo gobiernan dos cosas:
 * - el **guard** (`RequiresLegalConsentGuard`), que llama a `evaluate(uid)` al
 *   iniciar o restaurar sesión y espera la decisión del usuario;
 * - el **modal** (`LegalConsentModal`, montado una vez en `App.vue`), que muestra
 *   `mode` y llama a `accept()` / `decline()`.
 *
 * El resultado se guarda por `uid` para la sesión: el guard NO vuelve a leer en
 * cada navegación, sólo cuando cambia el usuario (login) o al recargar (restore).
 * La escritura del consentimiento la hace la Cloud Function `acceptLegalTerms`;
 * este store nunca escribe `consents/` ni las claves de aceptación.
 *
 * **No importa el store de auth** (el de auth sí importa éste): el cierre de
 * sesión de «Salir» lo inyecta quien llama (`decline(logout)`). Un ciclo de
 * imports entre los dos stores hacía que el bundler sacara la config de Firebase
 * del entry, y la aserción post-build de `hosting.sh` (ADR-022) lo rechazó.
 * @see specs/UX-UI/auth-flow.md
 */

export type LegalConsentMode = 'first' | 'update' | 'google-new'
export type LegalConsentStatus = 'idle' | 'checking' | 'required' | 'accepted'
export type LegalConsentOutcome = 'ok' | 'logout'

export interface ConsentAccountCard {
  name: string
  email: string
}

export const GENERIC_ACCEPT_ERROR = 'No pudimos registrar tu aceptación. Intenta de nuevo.'
export const OUTDATED_NOTICE =
  'Los términos se actualizaron mientras los revisabas. Revísalos y acepta la versión vigente.'

/**
 * Qué pide el guard según el estado leído — función PURA (se prueba sin Firebase).
 * `null` = no hay nada que pedir (acepta la vigente, o no hay versión vigente).
 */
export function decideConsentMode(
  current: CurrentLegalVersion | null,
  user: { exists: boolean; acceptedVersion: string | null }
): LegalConsentMode | null {
  if (current === null) return null
  if (!user.exists) return 'google-new'
  if (user.acceptedVersion === null) return 'first'
  if (user.acceptedVersion !== current.version_no) return 'update'
  return null
}

function accountCardFor(user: User | null): ConsentAccountCard | null {
  if (!user) return null
  const isGoogle = user.providerData?.some((p) => p.providerId === 'google.com')
  if (!isGoogle) return null
  return { name: user.displayName || user.email || '', email: user.email || '' }
}

export const useLegalConsentStore = defineStore('legalConsent', () => {
  const status = ref<LegalConsentStatus>('idle')
  const checkedUid = ref<string | null>(null)
  const mode = ref<LegalConsentMode | null>(null)
  const current = ref<CurrentLegalVersion | null>(null)
  const displayName = ref<string>('')
  const account = ref<ConsentAccountCard | null>(null)
  const submitting = ref(false)
  const errorMessage = ref('')
  /** Sube cada vez que el modal debe volver a pedir la casilla (versión cambiada). */
  const resetToken = ref(0)
  /** Flujo de consentimiento en curso (registro): el guard lo espera antes de evaluar. */
  const inFlight = ref<Promise<unknown> | null>(null)

  let resolveDecision: ((o: LegalConsentOutcome) => void) | null = null
  let pendingDecision: Promise<LegalConsentOutcome> | null = null

  const required = computed(() => status.value === 'required')

  function markAccepted(uid: string) {
    status.value = 'accepted'
    checkedUid.value = uid
    mode.value = null
    errorMessage.value = ''
  }

  function settle(outcome: LegalConsentOutcome) {
    const r = resolveDecision
    resolveDecision = null
    pendingDecision = null
    r?.(outcome)
  }

  /**
   * Decide si el usuario puede seguir. Devuelve `'ok'` o `'logout'` (eligió
   * «Salir»/«Cancelar»). Si hay que pedir el consentimiento, abre el modal y
   * la promesa queda pendiente hasta la decisión: no hay forma de seguir sin aceptar.
   */
  async function evaluate(uid: string): Promise<LegalConsentOutcome> {
    if (inFlight.value) {
      await inFlight.value.catch(() => undefined)
    }
    if (checkedUid.value === uid && status.value === 'accepted') return 'ok'
    if (checkedUid.value === uid && pendingDecision) return pendingDecision

    status.value = 'checking'
    let cur: CurrentLegalVersion | null
    let userState: Awaited<ReturnType<typeof fetchUserLegalState>>
    try {
      ;[cur, userState] = await Promise.all([fetchCurrentLegalVersion(), fetchUserLegalState(uid)])
    } catch (err) {
      // Sin datos no se puede decidir: se deja pasar ESTA navegación sin guardar
      // el resultado, así la siguiente vuelve a evaluar.
      console.error('[legalConsent] no se pudo leer el estado del consentimiento', err)
      status.value = 'idle'
      return 'ok'
    }

    if (cur === null) {
      // Decisión del owner: no bloquear por un documento ausente (fail-open).
      console.error('[legalConsent] legaldocs_current_version/current no existe: se deja pasar')
      markAccepted(uid)
      return 'ok'
    }

    const next = decideConsentMode(cur, userState)
    current.value = cur
    if (next === null) {
      markAccepted(uid)
      return 'ok'
    }

    const authUser = auth.currentUser as User | null
    mode.value = next
    checkedUid.value = uid
    displayName.value =
      userState.displayName || authUser?.displayName || authUser?.email?.split('@')[0] || ''
    account.value = next === 'google-new' ? accountCardFor(authUser) : null
    errorMessage.value = ''
    status.value = 'required'
    pendingDecision = new Promise<LegalConsentOutcome>((resolve) => {
      resolveDecision = resolve
    })
    return pendingDecision
  }

  /** «Continuar» del modal. Devuelve `true` si quedó aceptado. */
  async function accept(): Promise<boolean> {
    const authUser = auth.currentUser as User | null
    if (!authUser || !current.value || !mode.value) return false
    submitting.value = true
    errorMessage.value = ''
    try {
      if (mode.value === 'google-new') {
        // Se aprovisiona RECIÉN aquí (brief §5.2): antes de «Continuar» la cuenta
        // de Quizoteca no existe. Idempotente: si ya existe no se toca. Una sola
        // derivación del nombre para `users` y el espejo, y Auth después si Google
        // no dio nombre (ADR-023); quien muestra el nombre se refresca en el modal.
        await provisionProfessorAccount(authUser)
      }
      const channel: LegalChannel = mode.value === 'google-new' ? 'signup_google_modal' : 'gate_modal'
      await callAcceptLegalTerms(current.value.version_no, channel)
      markAccepted(authUser.uid)
      settle('ok')
      return true
    } catch (err) {
      if (getLegalTermsErrorCode(err) === 'TERMS_VERSION_OUTDATED') {
        await refreshCurrent()
        errorMessage.value = OUTDATED_NOTICE
      } else {
        console.error('[legalConsent] acceptLegalTerms falló', err)
        errorMessage.value = GENERIC_ACCEPT_ERROR
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  /** Relee la versión vigente y refresca el modo del modal. */
  async function refreshCurrent(): Promise<void> {
    try {
      const cur = await fetchCurrentLegalVersion()
      if (cur) {
        current.value = cur
        if (mode.value === 'first' || mode.value === 'update') {
          const uid = checkedUid.value
          const state = uid ? await fetchUserLegalState(uid) : null
          mode.value = state ? decideConsentMode(cur, state) ?? mode.value : mode.value
        }
      }
    } catch (err) {
      console.error('[legalConsent] no se pudo releer la versión vigente', err)
    }
    resetToken.value++
  }

  /** «Salir» / «Cancelar»: cierra la sesión. No hay forma de seguir sin aceptar. */
  async function decline(logout: () => Promise<unknown>): Promise<void> {
    submitting.value = true
    try {
      await logout()
    } finally {
      submitting.value = false
      reset()
      settle('logout')
    }
  }

  /**
   * Aceptación dentro del registro (casilla ya marcada, canal `signup_checkbox`).
   * El usuario YA está aprovisionado. Si falla, **no bloquea el registro**: el
   * guard mostrará el modal en la siguiente evaluación (brief §5.1).
   */
  function acceptDuringSignup(uid: string): Promise<void> {
    const run = (async () => {
      try {
        const cur = await fetchCurrentLegalVersion()
        if (!cur) {
          console.error('[legalConsent] sin versión vigente durante el registro')
          return
        }
        current.value = cur
        await callAcceptLegalTerms(cur.version_no, 'signup_checkbox')
        markAccepted(uid)
      } catch (err) {
        console.error('[legalConsent] la aceptación del registro falló; la pedirá el guard', err)
      }
    })()
    inFlight.value = run
    void run.finally(() => {
      if (inFlight.value === run) inFlight.value = null
    })
    return run
  }

  function reset() {
    status.value = 'idle'
    checkedUid.value = null
    mode.value = null
    account.value = null
    displayName.value = ''
    errorMessage.value = ''
    submitting.value = false
  }

  return {
    status,
    checkedUid,
    mode,
    current,
    displayName,
    account,
    submitting,
    errorMessage,
    resetToken,
    inFlight,
    required,
    evaluate,
    accept,
    decline,
    acceptDuringSignup,
    refreshCurrent,
    reset,
  }
})
